import React, { useState } from 'react';
import { Form, Input, Button, Card, Typography, Spin, Space } from 'antd';
import { UserOutlined, LockOutlined, LoginOutlined, HomeOutlined } from '@ant-design/icons';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import './LoginScreen.css';

// Title and Text not used in this component

interface LoginFormValues {
  email: string;
  password: string;
}

const LoginScreen: React.FC = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [userError, setUserError] = useState(false);
  const [pwdError, setPwdError] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const onFinish = async (values: LoginFormValues) => {
    setLoading(true);
    try {
      const result = await login(values.email, values.password);
      if (result.success) {
        // Get user data to check role
        const userData = localStorage.getItem('auth_user');
        if (userData) {
          try {
            const user = JSON.parse(userData);
            // Redirect based on role
            if (user.role === 'admin') {
              navigate('/admin');
            } else {
              navigate('/app');
            }
          } catch (e) {
            // Fallback to dashboard if parsing fails
            navigate('/app');
          }
        } else {
          navigate('/app');
        }
      } else {
        setUserError(true);
        setPwdError(true);
        form.setFields([{ name: 'password', errors: [result.error || 'Invalid email or password'] }]);
      }
    } catch (error) {
      console.error('Login error:', error);
      form.setFields([{ name: 'password', errors: ['An unexpected error occurred. Please try again.'] }]);
    } finally {
      setLoading(false);
    }
  };

  const handleValuesChange = () => {
    if (userError || pwdError) {
      setUserError(false);
      setPwdError(false);
      form.setFields([{ name: 'password', errors: [] }]);
    }
  };

  return (
    <div className="login-screen">
      {/* Blurred Background */}
      <div className="login-background">
        <div className="background-overlay" />
      </div>

      {/* Login Form */}
      <div className="login-container">
        <Card className="login-card" bordered={false} bodyStyle={{ padding: '12px 24px' }}>
          <Space direction="vertical" size={0} style={{ width: '100%', textAlign: 'center' }}>
            {/* Logo/Header */}
            <div className="login-header" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0, marginBottom: '8px' }}>
              <img
                src={process.env.PUBLIC_URL + '/sns-focus-logo.png'}
                alt="SNS Intelligence"
                style={{ width: 300, height: 'auto', objectFit: 'contain', maxWidth: '100%' }}
                onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
              />
            </div>

            {/* Login Form */}
            <Form
              form={form}
              name="login"
              onFinish={onFinish}
              onValuesChange={handleValuesChange}
              autoComplete="off"
              size="large"
              style={{ width: '100%' }}
            >
              <Form.Item
                name="email"
                validateStatus={userError ? 'error' : ''}
                help={userError ? '' : undefined}
                rules={[
                  { required: true, message: 'Please input your email!' },
                  { type: 'email', message: 'Please enter a valid email' }
                ]}
                style={{ marginBottom: '8px' }}
              >
                <Input
                  prefix={<UserOutlined />}
                  placeholder="Email"
                  disabled={loading}
                />
              </Form.Item>

              <Form.Item
                name="password"
                validateStatus={pwdError ? 'error' : ''}
                help={form.getFieldError('password')?.[0]}
                rules={[
                  { required: true, message: 'Please input your password!' },
                ]}
                style={{ marginBottom: '12px' }}
              >
                <Input.Password
                  prefix={<LockOutlined />}
                  placeholder="Password"
                  disabled={loading}
                />
              </Form.Item>

              <Form.Item style={{ marginBottom: 0 }}>
                <Button
                  type="primary"
                  htmlType="submit"
                  icon={<LoginOutlined />}
                  loading={loading}
                  block
                  size="large"
                  style={{
                    height: '44px',
                    fontSize: '16px',
                    fontWeight: 500,
                  }}
                >
                  {loading ? 'Signing In...' : 'Sign In'}
                </Button>
              </Form.Item>
            </Form>

            {/* Forgot Password Message */}
            <div style={{ 
              textAlign: 'center', 
              marginTop: '12px', 
              marginBottom: '8px'
            }}>
              <Typography.Text 
                type="secondary" 
                style={{ 
                  fontSize: '13px',
                  color: '#666'
                }}
              >
                Forgot your password? Please contact{' '}
                <a 
                  href="mailto:info@snsintel.com" 
                  style={{ color: '#1890ff' }}
                >
                  info@snsintel.com
                </a>
                {' '}to reset your password.
              </Typography.Text>
            </div>

            {/* Back to Home Button */}
            <Button
              type="text"
              icon={<HomeOutlined />}
              onClick={() => navigate('/')}
              style={{
                fontSize: '14px',
                color: '#8c8c8c',
                padding: '8px 16px',
                height: 'auto'
              }}
            >
              Back to Home
            </Button>
          </Space>
        </Card>
      </div>

      {/* Loading Overlay */}
      {loading && (
        <div className="loading-overlay">
          <Spin size="large" />
        </div>
      )}
    </div>
  );
};

export default LoginScreen;
